import React from "react";

import { Button, SplitButton, Tooltip, mergeClasses } from "@fluentui/react-components";
import { InfoRegular, ShareScreenStart24Filled, ShareScreenStop24Filled } from "@fluentui/react-icons";

import { useShare } from "../room/useShare";
import { useCssBreakpoints } from "react-use-css-breakpoints";
import PropTypes from "prop-types";
import { useUnavailableToolbarAction } from "./useUnavailableToolbarAction";
import { ReactComponent as ShareScreenOffIcon } from "../icons/ShareScreenOff.svg";
import { shouldShowScreenShareAction } from "./toolbar-screen-share-availability";

export default function ToolbarShareScreenButton({ styles, scene, hubChannel }) {
  const { canShareScreen, toggleShareScreen, isSharingScreen, screenShareActionState } = useShare(scene, hubChannel);
  const screenShareUnavailable = screenShareActionState.blocked
    ? screenShareActionState.unavailableReason || "Screen sharing is unavailable"
    : null;
  const notifyScreenShareUnavailable = useUnavailableToolbarAction(screenShareUnavailable);
  const onClick = event => {
    if (notifyScreenShareUnavailable(event)) {
      return;
    }

    toggleShareScreen();
  };
  const breakpoint = useCssBreakpoints();

  const isMobileBreakpoint = ["xs", "sm", "md"].includes(breakpoint);

  const shouldShowScreenShare = shouldShowScreenShareAction({
    canSpawnAndMoveMedia: hubChannel.can("spawn_and_move_media"),
    canShareScreen,
    isSharingScreen,
    screenShareHidden: screenShareActionState.hidden,
    isMobile: isMobileBreakpoint
  });

  if (!shouldShowScreenShare) return <></>;

  const infoActionButtonProps = {
    onClick,
    "aria-label": screenShareUnavailable
  };

  if (screenShareUnavailable) {
    return (
      <Tooltip content={screenShareUnavailable}>
        <SplitButton
          primaryActionButton={{
            onClick,
            "aria-disabled": true
          }}
          menuButton={infoActionButtonProps}
          appearance="subtle"
          className={mergeClasses(styles.buttonSplit, styles.buttonSplitDisabled)}
          icon={<ShareScreenOffIcon style={{ width: "24px", height: "24px", color: "var(--color-danger)" }} />}
          menuIcon={<InfoRegular style={{ width: "16px", height: "16px", marginBottom: "4px", color: "var(--color-text-primary)" }} />}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip content={isSharingScreen ? "Stop Screen Sharing" : "Share Screen"}>
      <Button
        appearance="subtle"
        onClick={onClick}
        className={isSharingScreen ? styles.buttonActive : styles.button}
        icon={
          isSharingScreen ? (
            <ShareScreenStop24Filled style={{ width: "24px", height: "24px" }} />
          ) : (
            <ShareScreenStart24Filled style={{ width: "24px", height: "24px" }} />
          )
        }
      />
    </Tooltip>
  );
}

ToolbarShareScreenButton.propTypes = {
  styles: PropTypes.object.isRequired,
  scene: PropTypes.object.isRequired,
  hubChannel: PropTypes.object.isRequired
};
